/**
 * Catalogue métier Clickorico — source unique pour la création / édition commerçant.
 * Chaque métier mappe vers enum Prisma `categorie` + profil business-profiles.js + defaults à la création.
 */

import { getBusinessProfile } from './business-profiles.js';

export const COMMERCE_FAMILIES = [
  {
    id: 'restauration',
    label: 'Restauration & food',
    icon: '🍽️',
    hint: 'Carte en ligne, commandes, livraison ou retrait',
  },
  {
    id: 'retail',
    label: 'Boutique & retail',
    icon: '🛍️',
    hint: 'Catalogue produits, stock, vente en ligne',
  },
  {
    id: 'beaute',
    label: 'Beauté & bien-être',
    icon: '💇',
    hint: 'Rendez-vous, prestations, planning',
  },
  {
    id: 'reservation_espaces',
    label: 'Terrains & sport',
    icon: '🎾',
    hint: 'Réservation de créneaux sur terrains ou salles',
  },
  {
    id: 'loisirs',
    label: 'Loisirs & activités',
    icon: '🎯',
    hint: 'Cours, stages, espaces, activités réservables',
  },
  {
    id: 'services_pro',
    label: 'Services professionnels',
    icon: '💼',
    hint: 'Consultations, RDV, services sur rendez-vous',
  },
  {
    id: 'autre',
    label: 'Autre',
    icon: '📦',
    hint: 'Configuration manuelle après création',
  },
];

const baseDefaults = () => ({
  moduleProduits: false,
  modulePrestations: false,
  moduleCaisse: false,
  devise: 'EUR',
  priseRDV: false,
  priseCreneaux: false,
  moduleCreneaux: false,
  reservationTable: false,
  livraison: false,
  clickAndCollect: false,
  paiementEnLigne: true,
  paiementSurPlace: true,
  pasGrilleMinutes: 15,
  dureesAutoriseesMinutes: [60, 90, 120],
});

/** Ancienne valeur erronée (paddle ≠ padel) — lecture des données existantes */
export const CATEGORIE_ID_ALIASES = { PADDLE: 'PADEL' };

export function normalizeCategorieId(categorieId) {
  const key = String(categorieId || '').toUpperCase();
  return CATEGORIE_ID_ALIASES[key] || key;
}

/** @type {Array<{ id: string, family: string, label: string, icon?: string, description?: string, profileKey: string, badge?: string, defaults: object, searchTerms?: string[] }>} */
export const COMMERCE_METIERS = [
  // —— Restauration ——
  { id: 'RESTAURANT', family: 'restauration', label: 'Restaurant', icon: '🍽️', profileKey: 'RESTAURANT', badge: 'Produits', searchTerms: ['resto', 'restaurant'], defaults: { ...baseDefaults(), moduleProduits: true, livraison: true, clickAndCollect: true, reservationTable: true } },
  { id: 'FOOD_TRUCK', family: 'restauration', label: 'Food truck', icon: '🚚', profileKey: 'RESTAURANT', badge: 'Itinérant', searchTerms: ['food truck', 'camion', 'itinérant'], defaults: { ...baseDefaults(), moduleProduits: true, livraison: true, clickAndCollect: true } },
  { id: 'PIZZERIA', family: 'restauration', label: 'Pizzeria', icon: '🍕', profileKey: 'PIZZERIA', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, livraison: true, clickAndCollect: true } },
  { id: 'BOULANGERIE', family: 'restauration', label: 'Boulangerie / Pâtisserie', icon: '🥖', profileKey: 'BOULANGERIE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true } },
  { id: 'CAFE', family: 'restauration', label: 'Café / Bar', icon: '☕', profileKey: 'CAFE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true, reservationTable: true } },
  { id: 'FAST_FOOD', family: 'restauration', label: 'Fast-food', icon: '🍔', profileKey: 'RESTAURANT', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, livraison: true, clickAndCollect: true } },
  { id: 'SUSHI', family: 'restauration', label: 'Restaurant sushi', icon: '🍱', profileKey: 'RESTAURANT', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, livraison: true, clickAndCollect: true } },
  { id: 'EPICERIE', family: 'restauration', label: 'Épicerie / Supermarché', icon: '🛒', profileKey: 'BOUTIQUE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true, livraison: true } },
  { id: 'EPICERIE_FINE', family: 'restauration', label: 'Épicerie fine', icon: '🧀', profileKey: 'BOUTIQUE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true, livraison: true } },
  { id: 'BOUCHERIE', family: 'restauration', label: 'Boucherie / Charcuterie', icon: '🥩', profileKey: 'BOUTIQUE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true, livraison: true } },
  { id: 'POISSONNERIE', family: 'restauration', label: 'Poissonnerie', icon: '🐟', profileKey: 'BOUTIQUE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true, livraison: true } },
  // Seed R3.1 — aussi en API ; présent ici pour fallback offline (jamais afficher le code TRAITEUR brut)
  { id: 'TRAITEUR', family: 'restauration', label: 'Traiteur', icon: '🥗', profileKey: 'RESTAURANT', badge: 'Produits', searchTerms: ['traiteur', 'buffet'], defaults: { ...baseDefaults(), moduleProduits: true, livraison: true, clickAndCollect: true } },

  // —— Retail ——
  { id: 'BOUTIQUE', family: 'retail', label: 'Boutique', icon: '🛍️', profileKey: 'BOUTIQUE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true, livraison: true } },
  { id: 'FLEURISTE', family: 'retail', label: 'Fleuriste', icon: '💐', profileKey: 'FLEURISTE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true, livraison: true } },
  { id: 'PRET_A_PORTER', family: 'retail', label: 'Prêt-à-porter / Mode', icon: '👔', profileKey: 'BOUTIQUE', badge: 'Produits', defaults: { ...baseDefaults(), moduleProduits: true, clickAndCollect: true } },

  // —— Beauté ——
  { id: 'COIFFURE', family: 'beaute', label: 'Salon de coiffure', icon: '✂️', profileKey: 'COIFFURE', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'BARBIER', family: 'beaute', label: 'Barbier', icon: '🧔', profileKey: 'BARBIER', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'SALON_BEAUTE', family: 'beaute', label: 'Institut de beauté', icon: '💅', profileKey: 'SALON_BEAUTE', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'NAIL_BAR', family: 'beaute', label: 'Nail bar', icon: '💅', profileKey: 'NAIL_BAR', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'SPA', family: 'beaute', label: 'Spa / Massage', icon: '🧖', profileKey: 'SPA', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },

  // —— Terrains & sport ——
  { id: 'PADEL', family: 'reservation_espaces', label: 'Club de padel', icon: '🏓', profileKey: 'PADEL', badge: 'Créneaux', description: 'Terrains de padel à durée libre', searchTerms: ['padel', 'paddle'], defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true } },
  { id: 'FOOT', family: 'reservation_espaces', label: 'Foot / Five', icon: '⚽', profileKey: 'FOOT', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true } },
  { id: 'TENNIS', family: 'reservation_espaces', label: 'Tennis', icon: '🎾', profileKey: 'TENNIS', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true } },
  { id: 'MULTISPORT', family: 'reservation_espaces', label: 'Multisport (terrains)', icon: '🏟️', profileKey: 'MULTISPORT', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true } },
  { id: 'GOLF', family: 'reservation_espaces', label: 'Golf', icon: '⛳', profileKey: 'GOLF', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'PISCINE', family: 'reservation_espaces', label: 'Piscine / Aquatique', icon: '🏊', profileKey: 'PISCINE', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },

  // —— Loisirs ——
  { id: 'CENTRE_EQUESTRE', family: 'loisirs', label: 'Centre équestre', icon: '🐴', profileKey: 'CENTRE_EQUESTRE', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true } },
  { id: 'SALLE_SPORT', family: 'loisirs', label: 'Salle de sport / Fitness', icon: '💪', profileKey: 'SALLE_SPORT', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'ESCALADE', family: 'loisirs', label: 'Escalade', icon: '🧗', profileKey: 'ESCALADE', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true } },
  { id: 'KARTING', family: 'loisirs', label: 'Karting', icon: '🏎️', profileKey: 'KARTING', badge: 'Créneaux', description: 'Créneaux courts (15–30 min)', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true, pasGrilleMinutes: 15, dureesAutoriseesMinutes: [15, 30, 45, 60] } },
  { id: 'ECOLE_MUSIQUE', family: 'loisirs', label: 'École de musique', icon: '🎵', profileKey: 'ECOLE_MUSIQUE', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'ECOLE_DANSE', family: 'loisirs', label: 'École de danse', icon: '💃', profileKey: 'ECOLE_DANSE', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'ATELIER_CREATIF', family: 'loisirs', label: 'Atelier créatif', icon: '🎨', profileKey: 'ATELIER_CREATIF', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'LASER_GAME', family: 'loisirs', label: 'Laser game / Paintball', icon: '🔫', profileKey: 'MULTISPORT', badge: 'Créneaux', searchTerms: ['paintball', 'laser'], defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true } },
  { id: 'ESCAPE_GAME', family: 'loisirs', label: 'Escape game', icon: '🧩', profileKey: 'MULTISPORT', badge: 'Créneaux', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true, moduleCreneaux: true } },

  // —— Services pro ——
  { id: 'CABINET_MEDICAL', family: 'services_pro', label: 'Cabinet médical', icon: '🩺', profileKey: 'CABINET_MEDICAL', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'SERVICE', family: 'services_pro', label: 'Service / Réparation', icon: '🔧', profileKey: 'SERVICE', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true, priseCreneaux: true } },
  { id: 'CONSULTANT', family: 'services_pro', label: 'Consultant / Pro', icon: '💼', profileKey: 'CABINET_MEDICAL', badge: 'RDV', defaults: { ...baseDefaults(), modulePrestations: true, priseRDV: true } },

  // —— Autre ——
  { id: 'AUTRE', family: 'autre', label: 'Autre activité', icon: '📦', profileKey: 'BOUTIQUE', badge: 'Mixte', defaults: { ...baseDefaults(), moduleProduits: true, modulePrestations: true, clickAndCollect: true } },
];

export function getCommerceMetier(categorieId) {
  if (!categorieId) return null;
  const key = normalizeCategorieId(categorieId);
  return COMMERCE_METIERS.find((m) => m.id === key) || null;
}

export function getCommerceFamily(familyId) {
  return COMMERCE_FAMILIES.find((f) => f.id === familyId) || null;
}

export function getMetiersByFamily(familyId) {
  return COMMERCE_METIERS.filter((m) => m.family === familyId);
}

export function getFamilyForCategorie(categorieId) {
  const metier = getCommerceMetier(categorieId);
  if (metier) return getCommerceFamily(metier.family);
  return getCommerceFamily('autre');
}

export function getCommerceMetierLabel(categorieId) {
  const metier = getCommerceMetier(categorieId);
  if (metier) return `${metier.icon || ''} ${metier.label}`.trim();
  const profile = getBusinessProfile(categorieId);
  if (profile?.label) return `${profile.icon || ''} ${profile.label}`.trim();
  // Jamais renvoyer le code UPPERCASE brut (ex. TRAITEUR_CUSTOM)
  const raw = String(categorieId || '').trim();
  if (!raw) return '—';
  const humanized = raw
    .split(/_+/)
    .filter(Boolean)
    .map((p, i) => {
      const lower = p.toLowerCase();
      if (i > 0 && ['de', 'du', 'des', 'et', 'la', 'le', 'les'].includes(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
  return humanized || '—';
}

export function searchMetiers(query, familyId = null) {
  const q = String(query || '').trim().toLowerCase();
  let list = familyId ? getMetiersByFamily(familyId) : COMMERCE_METIERS;
  if (!q) return list;
  return list.filter((m) => {
    const hay = [m.label, m.id, m.description, ...(m.searchTerms || [])].join(' ').toLowerCase();
    return hay.includes(q);
  });
}

/** Libellés des modules activés (récap création) */
export function describeDefaults(defaults) {
  const lines = [];
  if (defaults.moduleProduits) lines.push('Catalogue produits');
  if (defaults.modulePrestations) lines.push('Prestations & services');
  if (defaults.moduleCreneaux) lines.push('Réservation par créneaux (terrains / salles)');
  else if (defaults.priseCreneaux) lines.push('Prise de créneaux');
  if (defaults.priseRDV) lines.push('Prise de rendez-vous');
  if (defaults.reservationTable) lines.push('Réservation de table');
  if (defaults.livraison) lines.push('Livraison');
  if (defaults.clickAndCollect) lines.push('Click & Collect');
  return lines;
}

export function getDefaultsForCategorie(categorieId) {
  const metier = getCommerceMetier(categorieId);
  return metier ? { ...metier.defaults } : { ...baseDefaults(), moduleProduits: true };
}

/** Union des defaults pour plusieurs métiers cochés. Liste vide = baseDefaults (pas de RESTAURANT implicite). */
export function getDefaultsForCategories(categorieIds) {
  const list = Array.isArray(categorieIds)
    ? categorieIds.map(normalizeCategorieId).filter(Boolean)
    : [];
  const merged = { ...baseDefaults() };
  if (list.length === 0) {
    return merged;
  }
  const durees = new Set();
  for (const id of list) {
    const d = getDefaultsForCategorie(id);
    for (const [key, value] of Object.entries(d)) {
      if (typeof value === 'boolean') {
        if (value) merged[key] = true;
      } else if (Array.isArray(value)) {
        value.forEach((v) => durees.add(v));
      } else if (key === 'pasGrilleMinutes' && value) {
        merged.pasGrilleMinutes = Math.min(merged.pasGrilleMinutes || value, value);
      } else if (key === 'devise' && value) {
        merged.devise = value;
      }
    }
  }
  if (durees.size > 0) {
    merged.dureesAutoriseesMinutes = [...durees].sort((a, b) => a - b);
  }
  return merged;
}

export function getCommercantCategoryIds(commercant) {
  const fromArray = Array.isArray(commercant?.categories) ? commercant.categories : [];
  const primary = commercant?.categorie ? [commercant.categorie] : [];
  return [...new Set([...fromArray, ...primary].map(normalizeCategorieId).filter(Boolean))];
}

/** Filtre liste commerçants : options par famille */
export function getFilterOptionsByFamily() {
  return COMMERCE_FAMILIES.map((family) => ({
    familyId: family.id,
    label: `${family.icon} ${family.label}`,
    metiers: getMetiersByFamily(family.id).map((m) => m.id),
  }));
}

export function commercantMatchesFamilyFilter(commercant, familyId) {
  if (!familyId || familyId === 'all') return true;
  return getCommercantCategoryIds(commercant).some(
    (id) => getCommerceMetier(id)?.family === familyId,
  );
}
